/**
 * Analytics Tracking Utility
 * Google Analytics 4 (G-GM9FFLZ8LK) + Meta Pixel
 */

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
    fbq?: (...args: any[]) => void;
  }
}

type EventParams = {
  [key: string]: string | number | boolean | undefined;
};

/**
 * Track a custom event in Google Analytics 4
 */
export const trackEvent = (
  eventName: string,
  params?: EventParams
): void => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", eventName, params);
    console.log(`[GA4] Event tracked: ${eventName}`, params);
  }
};

/**
 * Track a Meta Pixel event
 */
export const trackMetaEvent = (
  eventName: string,
  params?: Record<string, any>
): void => {
  if (typeof window !== "undefined" && window.fbq) {
    window.fbq("track", eventName, params);
    console.log(`[Meta Pixel] Event tracked: ${eventName}`, params);
  }
};

/**
 * Track a custom Meta Pixel event
 */
export const trackMetaCustomEvent = (
  eventName: string,
  params?: Record<string, any>
): void => {
  if (typeof window !== "undefined" && window.fbq) {
    window.fbq("trackCustom", eventName, params);
    console.log(`[Meta Pixel] Custom event tracked: ${eventName}`, params);
  }
};

/**
 * Track phone call request submission (conversion)
 */
export const trackPhoneCallRequest = (params?: {
  phone_number?: string;
  first_name?: string;
  source?: string;
}): void => {
  // GA4 tracking
  trackEvent("generate_lead", {
    currency: "USD",
    value: 50,
    lead_source: params?.source || "hero_form",
    ...params,
  });
  trackEvent("phone_call_request", {
    source: params?.source || "hero_form",
  });

  // Meta Pixel - Lead event
  trackMetaEvent("Lead", {
    content_name: "Phone Call Request",
    content_category: "Solar Consultation",
    value: 50.00,
    currency: "USD",
  });
};

/**
 * Track successful phone call connection
 */
export const trackPhoneCallSuccess = (params?: {
  source?: string;
}): void => {
  trackEvent("phone_call_connected", {
    source: params?.source || "hero_form",
  });

  // Meta Pixel - Custom event for call connected
  trackMetaCustomEvent("PhoneCallConnected", {
    source: params?.source || "hero_form",
  });
};

/**
 * Track booking calendar interaction
 */
export const trackBookingStarted = (): void => {
  trackEvent("begin_checkout", {
    currency: "USD",
    value: 100,
  });

  // Meta Pixel - InitiateCheckout
  trackMetaEvent("InitiateCheckout", {
    content_name: "Solar Consultation Booking",
    content_category: "Booking",
    value: 100.00,
    currency: "USD",
  });
};

/**
 * Track successful booking submission (conversion)
 */
export const trackBookingComplete = (params?: {
  date?: string;
  time?: string;
}): void => {
  trackEvent("purchase", {
    currency: "USD",
    value: 100,
    transaction_id: `booking_${Date.now()}`,
    ...params,
  });
  trackEvent("booking_complete", params);

  // Google Ads conversion tracking (AW-17770407681)
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "conversion", {
      send_to: "AW-17770407681/x6WLCL3Jg-AbEIHOy51C",
    });
    console.log("[Google Ads] Conversion tracked: booking_complete");
  }

  // Meta Pixel - Schedule event (standard) + CompleteRegistration
  trackMetaEvent("Schedule", {
    content_name: "Solar Consultation",
    content_category: "Booking",
    value: 100.00,
    currency: "USD",
  });
  trackMetaEvent("CompleteRegistration", {
    content_name: "Booking Complete",
    status: true,
  });
};

/**
 * Track phone number click (call tracking)
 */
export const trackPhoneClick = (params?: {
  phone_number?: string;
  source?: string;
}): void => {
  trackEvent("click", {
    event_category: "engagement",
    event_label: "phone_call",
    ...params,
  });

  // Meta Pixel - Contact event
  trackMetaEvent("Contact", {
    content_name: "Phone Click",
    content_category: params?.source || "header",
  });
};

/**
 * Track calculator interaction
 */
export const trackCalculatorUsed = (params?: {
  monthly_bill?: number;
  estimated_savings?: number;
}): void => {
  trackEvent("calculator_used", params);

  // Meta Pixel - Custom event for calculator
  trackMetaCustomEvent("CalculatorUsed", {
    monthly_bill: params?.monthly_bill,
    estimated_savings: params?.estimated_savings,
  });
};

/**
 * Track CTA button clicks
 */
export const trackCTAClick = (params: {
  button_text: string;
  button_location: string;
}): void => {
  trackEvent("cta_click", params);

  // Meta Pixel - ViewContent for engagement
  trackMetaEvent("ViewContent", {
    content_name: params.button_text,
    content_category: params.button_location,
  });
};

// Scroll depth tracking state
let scrollMilestones = new Set<number>();

/**
 * Track scroll depth (with deduplication)
 */
export const trackScrollDepth = (depth: number): void => {
  // Only track at milestones: 25%, 50%, 75%, 90%, 100%
  const milestones = [25, 50, 75, 90, 100];
  const milestone = milestones.find(m => depth >= m && !scrollMilestones.has(m));
  
  if (milestone) {
    scrollMilestones.add(milestone);
    trackEvent("scroll", {
      percent_scrolled: milestone,
    });
    
    // Meta Pixel for deep engagement
    if (milestone >= 75) {
      trackMetaCustomEvent("DeepScroll", {
        depth: milestone,
      });
    }
  }
};

/**
 * Track time on page
 */
export const trackTimeOnPage = (seconds: number): void => {
  // Track at intervals: 30s, 60s, 120s, 300s
  const intervals = [30, 60, 120, 300];
  if (intervals.includes(seconds)) {
    trackEvent("time_on_page", {
      seconds,
      minutes: Math.floor(seconds / 60),
    });
  }
};

/**
 * Track section views (when user scrolls to a section)
 */
export const trackSectionView = (sectionId: string): void => {
  trackEvent("section_view", {
    section_id: sectionId,
  });
};

/**
 * Track exit intent trigger
 */
export const trackExitIntent = (triggerType: string): void => {
  trackEvent("exit_intent_triggered", {
    trigger_type: triggerType,
  });
  
  trackMetaCustomEvent("ExitIntent", {
    trigger_type: triggerType,
  });
};

/**
 * Track popup interactions
 */
export const trackPopupInteraction = (action: 'shown' | 'closed' | 'submitted', popupType: string): void => {
  trackEvent("popup_interaction", {
    action,
    popup_type: popupType,
  });
};

/**
 * Track form field interactions
 */
export const trackFormFieldFocus = (fieldName: string, formSource: string): void => {
  trackEvent("form_field_focus", {
    field_name: fieldName,
    form_source: formSource,
  });
};

/**
 * Track page navigation
 */
export const trackPageView = (pagePath: string, pageTitle?: string): void => {
  trackEvent("page_view", {
    page_path: pagePath,
    page_title: pageTitle,
  });
  
  trackMetaEvent("PageView", {
    page_path: pagePath,
  });
};

/**
 * Reset scroll tracking (call on route change)
 */
export const resetScrollTracking = (): void => {
  scrollMilestones = new Set<number>();
};

/**
 * Track video engagement
 */
export const trackVideoEngagement = (params: {
  video_title?: string;
  action: "play" | "pause" | "complete";
}): void => {
  trackEvent("video_" + params.action, {
    video_title: params.video_title,
  });

  // Meta Pixel - Custom event for video views
  if (params.action === "play") {
    trackMetaCustomEvent("VideoPlay", {
      video_title: params.video_title,
    });
  } else if (params.action === "complete") {
    trackMetaCustomEvent("VideoComplete", {
      video_title: params.video_title,
    });
  }
};
